import global from '@/common/global';

/**
 * @class {Lazy}
 * @desc {用于懒加载收集v-lazy元素数据，集中管理}
 */
class Lazy {
    constructor() {
        this.els = [];
    }
    checkInViews() {
        return this.els.filter((el) => {
            if (el._loaded) return false;
            return isInView(el._host);
        });
    }
    addEl(el) {
        this.els.push(el);
    }

    removeEl(el) {
        for (let i = 0; i < this.els.length; i++) {
            if (el === this.els[i]._host) {
                this.els.splice(i, 1);
                break;
            }
        }
    }

    addEvent(el, event, callback = null) {
        el.addEventListener(event, callback, false);
    }

    removeEvent(el, event, callback = null) {
        el.removeEventListener(event, callback, false);
    }

    shouldLoad() {
        let needLoadEls = this.checkInViews();
        let invoked = false;
        needLoadEls.map((child) => {
            if (child.callback && !child._loaded) {
                // externally determine whether the current item has been loaded
                child._loaded = child.callback(child.context);
                invoked = true;
            }
        });
        return invoked;
    }

    shouldLoadItem(elItem) {
        // 没有加载并且在视图内
        if (!elItem._loaded && isInView(elItem._host)) {
            elItem.callback &&
                (elItem._loaded = elItem.callback(elItem.context));
        }
    }
}
Lazy.eventMap = {};
let lazyClass = new Lazy();

export default (Vue) => {
    /**
     * use:
     *  <ul v-lazy-container="{trigger:['scroll','transitionend']}">
     *      <li v-lazy="{context:item,callback:func}" v-for="(item,index) in data"></li>
     *  </ul>
     *
     *  methods:{
     *      // 确保item类型[object Object]
     *      async func(item){
     *          // request here
     *          let res = await axios.post('/api',{params})
     *          if(res){
     *              Object.assign(item,res)
     *              return true
     *          }
     *          return false;
     *          // 返回一个true、false表示当前lazy项是否加载完毕（成功）,修改的是item._loaded
     *      }
     *  }
     *
     *  tips:可添加多个v-lazy-container指令，用于在不同容器，不同的方式触发lazyload,
     * 		 必须绑定唯一key，每次异步更新数据key都保持唯一（不能为之前使用过的）
     */

    /**
     * v-lazy="{contenxt:item,callback:func,}"
     * @params {object} {context:data,callback(arg:context),_loaded,_host}
     * @params context 需要应用的上下文，
     *      @description 如果存在上下文，默认在v-lazy-container trigger中的事件触发的时候，调用回调，并回传上下文，根据回调函数的返回值确认异步请求是否成功，如果成功则不会被二次触发回调，如果不成功尝试多次回调
     *      @description 如果没有这个参数，默认监听滚动并且元素在视图内就触发回调，多次触发
     */
    Vue.directive('lazy', {
        bind(el, binding) {
            if (
                Object.prototype.toString.call(binding.value) !==
                '[object Object]'
            ) {
                throw Error('args type error: must be object');
            } else {
                if (binding.value['context']) {
                    Vue.nextTick(() => {
                        const elItem = Object.assign(binding.value, {
                            _host: el,
                            _loaded: false
                        });
                        lazyClass.addEl(elItem);
                        lazyClass.shouldLoadItem(elItem);
                    });
                }
            }
        },
        inserted(el, binding) {
            // 没有上下文默认滚动触发回调，不做数据收集兼容v-scrollvisible指令
            if (!binding.value['context']) {
                const { callback, options } = binding.value;
                let ticking = false;
                el.__vueScrollVisible__ = function () {
                    if (!ticking) {
                        // 防止抖动
                        window.requestAnimationFrame(function () {
                            let res = isOutoffViewPoint(el, options);
                            callback(res);
                            ticking = false;
                        });
                    }
                    ticking = true;
                };
                global.scroll(el.__vueScrollVisible__);
            }
        },
        unbind(el) {
            if (el.__vueScrollVisible__) {
                global.scroll(el.__vueScrollVisible__, false);
                delete el.__vueScrollVisible__;
            } else {
                lazyClass.removeEl(el);
            }
        }
    });

    /**
     * v-lazy-container
     * @params {object} {trigger:['scroll']} 触发回调的事件名称，默认为scroll
     */
    Vue.directive('lazy-container', {
        bind(el, binding, vnode) {
            Vue.nextTick(() => {
                // when there is no trigger mode, the default is scroll
                const trigger = binding.value['trigger'] || ['scroll'];
                trigger &&
                    trigger.map((eventName) => {
                        const fn = debounce(() => {
                            lazyClass.shouldLoad();
                        });
                        if (!Lazy.eventMap[eventName])
                            Lazy.eventMap[eventName] = [];
                        Lazy.eventMap[eventName].unshift({ el, fn });
                        lazyClass.addEvent(el, eventName, fn);
                    });
            });
        },
        inserted(el) {
            let frameId;
            function checkContainerInView() {
                if (isInView(el)) {
                    // 首次加载，检测元素在视图内并且调用了回调函数就取消监听
                    let invoked = lazyClass.shouldLoad();
                    if (invoked) {
                        cancelAnimationFrame(frameId);
                    } else {
                        frameId = requestAnimationFrame(checkContainerInView);
                    }
                } else {
                    frameId = requestAnimationFrame(checkContainerInView);
                }
            }
            frameId = requestAnimationFrame(checkContainerInView);
        },
        unbind(el, binding) {
            // unbind event
            Object.keys(Lazy.eventMap).map((name) => {
                const currentLazyEventMaps = Lazy.eventMap[name];
                for (let i = 0; i < currentLazyEventMaps.length; i++) {
                    if (el === currentLazyEventMaps[i].el) {
                        lazyClass.removeEvent(
                            el,
                            name,
                            currentLazyEventMaps[i].fn
                        );
                        currentLazyEventMaps.splice(i--, 1);
                    }
                }
            });
        }
    });
};

function isInView(el) {
    var { top, right, bottom, left } = el.getBoundingClientRect();
    var width = window.innerWidth;
    var height = window.innerHeight;
    if (bottom < 0 || top > height) {
        return false;
    }
    if (right < 0 || left > width) {
        return false;
    }
    return true;
}

function debounce(fn, delay = 300) {
    let timer = null;
    return function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            fn.apply(this, [...arguments]);
        }, delay);
    };
}

function isOutoffViewPoint(el, options) {
    // 获取元素相对视图位置
    let res = el.getBoundingClientRect();
    let isOutOffView = {
        // 元素高度加元素相对顶部高度小于 0 说明是顶部不可见
        invisibleOnTop: res.top + res.height < 0,
        // 元素相对顶部距离大于视图高度说明底部不可见
        invisibleOnBottom: res.top > window.innerHeight
    };

    return Object.assign(res, {
        isOutOffView
    });
}
